import { useState } from "react";
import { getPrice } from "../../utils/translator";
import { clientConfig } from "../../config";
import { Translate, tierFullName, tierName } from "../ChoosePlanCarePlus/planContent";
import { CarePlusMedicationLine } from "../ChoosePlanCarePlus/useChoosePlanCarePlus";
import { planCoversTreatmentAndService as resolvePlanCoversTreatmentAndService } from "./carePlusCartTotals";

// HL-9781 §Phase 5: Shipping page "Your Cart" card for Care+ orders (Figma 4090:37750 / 4070:32277).
// Deliberately separate from CartViewDE/CartView/OTCCartView — those stay untouched for every
// other DE/CH flow. Data-only props (no cart-context reads here): `medication` comes in as a prop.

interface CarePlusShippingCartCardProps {
  t: Translate;
  medication: CarePlusMedicationLine | null;
  planFee: number;
  /** Plan row amount (plan fee + today's shipping when delivery is bundled). Falls back to planFee. */
  planDisplayPrice?: number;
  planTier: string;
  periodMonths: number;
  planImage: { base_path: string; name: string } | null;
  total: number;
  discount: number;
  finalPrice: number;
  // Today and recurring medication display totals can include different configured fee parts.
  medicationTotal: number;
  monthlyMedicationTotal: number;
  // Treatment costs — its own row for a one-time purchase (a plan order has treatment in the
  // plan fee, so this is 0 and the row is hidden).
  treatmentFee: number;
  // One-time/no-plan Care+ purchases itemise service rather than including it in medication.
  serviceFee: number;
  // The shipping fee is shown as its own row rather than being bundled into the medication price.
  // For a plan order it sits below the plan; for a one-time purchase below the treatment row.
  // 0 renders the row as free shipping.
  shippingFee: number;
  monthlyTreatmentFee: number;
  monthlyServiceFee: number;
  monthlyShippingFee: number;
  showTreatmentFee: boolean;
  showServiceFee: boolean;
  showShippingFee: boolean;
  showMonthlyTreatmentFee: boolean;
  showMonthlyServiceFee: boolean;
  showMonthlyShippingFee: boolean;
  currencyLocale: string;
  currencyRate: number;
  // Prescription-only: no medication and no plan — show the treatment fee line
  // instead of an all-zero "Plan / monthly payment" row.
  isPrescriptionOnly?: boolean;
  hideNextMonthDetails?: boolean;
  defaultShowNextMonthDetails?: boolean;
  compact?: boolean;
  discountType?: 'first_month' | 'recurring' | null;
  // PaymentV3CarePlus shows the customer-facing plan name ("Care+ Pro"); the older shipping pages
  // keep the short tier ("Pro"), so the label stays opt-in rather than changing under them.
  fullTierName?: boolean;
}

const CarePlusShippingCartCard = ({
  t,
  medication,
  planFee,
  planDisplayPrice,
  planTier,
  periodMonths,
  planImage,
  total,
  discount,
  finalPrice,
  medicationTotal,
  monthlyMedicationTotal = 0,
  treatmentFee = 0,
  serviceFee = 0,
  shippingFee = 0,
  monthlyTreatmentFee = 0,
  monthlyServiceFee = 0,
  monthlyShippingFee = 0,
  showTreatmentFee = false,
  showServiceFee = false,
  showShippingFee = false,
  showMonthlyTreatmentFee = false,
  showMonthlyServiceFee = false,
  showMonthlyShippingFee = false,
  currencyLocale,
  currencyRate,
  isPrescriptionOnly = false,
  hideNextMonthDetails = false,
  defaultShowNextMonthDetails = false,
  compact = false,
  discountType = null,
  fullTierName = false,
}: CarePlusShippingCartCardProps) => {
  const [showNextMonthDetails, setShowNextMonthDetails] = useState(defaultShowNextMonthDetails);
  // Callers must pass planDisplayPrice; fall back to planFee so a missing prop never renders "NaN €".
  const resolvedPlanDisplayPrice = Number.isFinite(planDisplayPrice)
    ? Number(planDisplayPrice)
    : (Number(planFee) || 0);
  const price = (amount: number) => getPrice(Number(amount) || 0, currencyLocale, currencyRate);
  const shippingPrice = (amount: number) => amount > 0 ? price(amount) : t("free_delivery", "Free");

  // Pro/Max: the plan price shown above already includes treatment, service and the first month's
  // delivery, so "Next monthly payment" stays medication + delivery only.
  const planCoversTreatmentAndService = resolvePlanCoversTreatmentAndService(planFee, planTier);
  // Basic (monthly) plan: next month bills the plan again, so the recurring pane shows the same
  // single plan row as "Today" instead of itemising treatment / service / delivery.
  const nextMonthRepeatsPlan = !planCoversTreatmentAndService && (showMonthlyTreatmentFee || showMonthlyServiceFee);
  // Only the fees not bundled into the medication price (displayed_price_components) count.
  const nextMonthPlanPrice = (showMonthlyTreatmentFee ? monthlyTreatmentFee : 0)
    + (showMonthlyServiceFee ? monthlyServiceFee : 0)
    + (showMonthlyShippingFee ? monthlyShippingFee : 0);

  const planRow = (amount: number, testId?: string) => (
    <div className="flex w-full items-start justify-between" data-testid={testId}>
      <div className="flex items-center gap-[9px]">
        {planImage && (
          <img
            src={`${clientConfig.IMAGES_URL}${planImage.base_path}/${encodeURIComponent(planImage.name)}`}
            alt={t("monthly_plan", "Monthly plan")}
            className="h-[47px] w-[52px] shrink-0 rounded-[4px] object-cover"
          />
        )}
        <div className="flex flex-col">
          <span className="text-[16px] font-medium text-[#08080a]">
            {planTier === "monthly"
              ? t("plan_row_monthly", "Monthly Plan")
              : [(fullTierName ? tierFullName : tierName)(planTier, t), t("plan_suffix", "Plan")].filter(Boolean).join(" ")}
          </span>
          <span className="text-[12px] text-[#79716b]">
            {periodMonths > 1 ? t("cadence_n_month_plan", "{{n}} month plan").replace("{{n}}", String(periodMonths)) : t("cadence_monthly", "Pay every month")}
          </span>
          <span className="mt-[2px] text-[12px] text-[#79716b]">
            ({planCoversTreatmentAndService
              ? t("delivery_included_first_month", "incl. delivery 1st month")
              : t("delivery_included", "Delivery included")})
          </span>
        </div>
      </div>
      <span className="text-[18px] font-medium text-[#08080a]">{price(amount)}</span>
    </div>
  );

  return (
    <div className="flex w-full flex-col items-center gap-[22px] overflow-hidden rounded-[13px] border border-solid border-[#dee3ed] bg-white p-[15px]">
      <div className="w-full text-[16px] font-medium text-[#08080a]">{t("your_cart", "Your Cart")}</div>

      <div className="flex w-full flex-col gap-[9px]">
        <div className="flex w-full flex-col gap-[12px]">
          <div className="text-[12px] font-medium text-[#99a3b2]">{t("today", "Today:")}</div>

          <div className="flex w-full flex-col gap-[18px]">
            {medication && (
              <div className="flex w-full items-center justify-between" data-testid="care-plus-medication-row">
                <div className={`flex items-center gap-[9px] ${compact ? "min-w-0 flex-1" : ""}`}>
                  <img
                    src={medication.imageSrc}
                    alt={medication.title}
                    className="h-[47px] w-[52px] shrink-0 rounded-[4px] object-cover"
                  />
                  <div className={`flex flex-col ${compact ? "min-w-0 flex-1" : ""}`}>
                    <span className={`text-[16px] font-medium text-[#08080a] ${compact ? "min-w-0 leading-[1.2]" : ""}`}>{medication.title}</span>
                    <span className={`text-[12px] text-[#79716b] ${compact ? "mt-[2px]" : ""}`}>{medication.quantityStr}</span>
                  </div>
                </div>
                <div className="flex shrink-0 flex-col items-end gap-[2px]">
                  <span className="text-[18px] font-medium text-[#08080a]">{price(medicationTotal)}</span>
                  <div className="flex items-center gap-[11px] text-[10px]">
                    <span className="font-medium text-[#08080a]">x1</span>
                    <span className="text-[#616975]">{price(medicationTotal)}</span>
                  </div>
                </div>
              </div>
            )}

            {isPrescriptionOnly ? (
              <div className="flex w-full items-start justify-between">
                <div className="flex flex-col">
                  <span className="text-[16px] font-medium text-[#08080a]">
                    {t("treatment_costs", "Treatment costs")}
                  </span>
                  <span className="text-[12px] text-[#79716b]">
                    {t("desc_prescription_only", "Treatment fee only, no plan,")}
                  </span>
                  <span className="text-[12px] font-semibold text-[#79716b]">
                    {t("desc_prescription_only_delivery", "delivery can take up to 14 days")}
                  </span>
                </div>
                <span className="text-[18px] font-medium text-[#08080a]">{price(finalPrice + discount)}</span>
              </div>
            ) : planFee > 0 ? (
              // Hide the Plan row entirely for a one-time Care+ order (no subscription → plan fee 0).
              planRow(resolvedPlanDisplayPrice)
            ) : null}

            {/* One-time/no-plan orders itemise fees not bundled by displayed_price_components. */}
            {!isPrescriptionOnly && planFee === 0 && showTreatmentFee && treatmentFee > 0 && (
              <div className="flex w-full items-start justify-between" data-testid="care-plus-one-time-treatment-fee">
                <span className="text-[16px] font-medium text-[#08080a]">{t("treatment_fee", "Treatment fee")}</span>
                <span className="text-[18px] font-medium text-[#08080a]">{price(treatmentFee)}</span>
              </div>
            )}
            {!isPrescriptionOnly && planFee === 0 && showServiceFee && serviceFee > 0 && (
              <div className="flex w-full items-start justify-between" data-testid="care-plus-one-time-service-fee">
                <span className="text-[16px] font-medium text-[#08080a]">{t("service_fee", "Service fee")}</span>
                <span className="text-[18px] font-medium text-[#08080a]">{price(serviceFee)}</span>
              </div>
            )}
            {!isPrescriptionOnly && planFee === 0 && showShippingFee && (
              <div className="flex w-full items-start justify-between" data-testid="care-plus-one-time-shipping-fee">
                <span className="text-[16px] font-medium text-[#08080a]">{t("delivery", "Delivery")}</span>
                <span className="text-[18px] font-medium text-[#08080a]">{shippingPrice(shippingFee)}</span>
              </div>
            )}
          </div>
        </div>

        {/* Only the month-to-month plans (Care+ basic, V3 monthly) show the "plan + medication monthly" note. */}
        {!isPrescriptionOnly && planFee > 0 && (planTier === "basic" || planTier === "monthly") && (
          <p className="w-full text-[12px] text-[#99a3b2]">{t("plan_medication_monthly_note", "Plan + medication each month until you cancel")}</p>
        )}
      </div>

      <div className="h-0 w-full border-t border-dashed border-[#dee3ed]" />

      <div className="flex w-full flex-col gap-[23px]">
        <div className="flex w-full flex-col gap-[10px] text-[14px]">
          <div className="flex flex-col gap-[4px]">
            <div className="flex w-full items-center justify-between text-[17px] text-[#08080a]" data-testid="care-plus-total-row">
              <span>{t("total", "Total:")}</span>
              <span>{price(total)}</span>
            </div>
            {discount > 0 && (
              <div className="flex w-full items-center justify-between font-medium text-[#61a333]" data-testid="care-plus-discount-row">
                <span>{t("discount", "Discount:")}</span>
                <span>- {price(discount)}</span>
              </div>
            )}
          </div>
          <div className="flex w-full items-center justify-between text-[20px] font-medium text-[#08080a]" data-testid="care-plus-final-price-row">
            <span>{t("final_price_today", "Final price today")}:</span>
            <span>{price(finalPrice)}</span>
          </div>
        </div>

        {!hideNextMonthDetails && medication && planFee > 0 && (
          <div className="flex w-full flex-col gap-[10px]">
            <button
              type="button"
              onClick={() => setShowNextMonthDetails((prev) => !prev)}
              className="flex w-full items-center justify-between border-none bg-transparent p-0 text-[12px] text-[#99a3b2]"
            >
              <span>{t("next_month_payment", "Next month's payment:")}</span>
              <span className="underline">{showNextMonthDetails ? t("hide_details", "Hide details") : t("show_details", "Show details")}</span>
            </button>

            {showNextMonthDetails && (
              <div className="flex w-full flex-col gap-[18px]" data-testid="care-plus-next-month-breakdown">
                <div className="flex w-full items-center justify-between">
                  <div className="flex items-center gap-[9px]">
                    <img
                      src={medication.imageSrc}
                      alt={medication.title}
                      className="h-[47px] w-[52px] shrink-0 rounded-[4px] object-cover"
                    />
                    <div className="flex flex-col">
                      <span className="text-[16px] font-medium text-[#08080a]">{medication.title}</span>
                      <span className="text-[12px] text-[#79716b]">{medication.quantityStr}</span>
                    </div>
                  </div>
                  <div className="flex flex-col items-end gap-[2px]">
                    <span className="text-[18px] font-medium text-[#08080a]">{price(monthlyMedicationTotal)}</span>
                    <span className="text-[10px] text-[#616975]">{t("monthly", "monthly")}</span>
                  </div>
                </div>

                {nextMonthRepeatsPlan && planRow(nextMonthPlanPrice, "care-plus-next-month-plan-row")}

                {!nextMonthRepeatsPlan && showMonthlyShippingFee && (
                  <div className="flex w-full items-start justify-between" data-testid="care-plus-monthly-shipping-fee">
                    <span className="text-[16px] font-medium text-[#08080a]">{t("delivery", "Delivery")}</span>
                    <span className="text-[18px] font-medium text-[#08080a]">{shippingPrice(monthlyShippingFee)}</span>
                  </div>
                )}
                {discountType === 'recurring' && discount > 0 && (
                  <div className="flex w-full items-start justify-between text-[#61a333]">
                    <span className="text-[16px] font-medium">{t("discount", "Discount:")}</span>
                    <span className="text-[18px] font-medium">-{price(discount)}</span>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        <div className="flex w-full flex-col items-center gap-px rounded-[6px] bg-[#f6f7f9] px-[31px] py-[9px] text-center text-[12px]">
          <p
            className="w-full bg-clip-text font-medium text-transparent"
            style={{ backgroundImage: "linear-gradient(90deg, #269c6e 0%, #7aba47 100%)" }}
          >
            {t("all_costs_included", "All costs included")}
          </p>
          <p className="w-full text-[#656d79]">{t("all_costs_desc", "Medication costs - Service fee - VAT - Delivery")}</p>
        </div>
      </div>
    </div>
  );
};

export default CarePlusShippingCartCard;
